/**
 * Zod's own sentences, in the words zod 3 used.
 *
 * Every request body is parsed by a zod schema, and a field that fails with no
 * message of its own reaches the person at the keyboard in zod's words:
 * `errorHandler` sends the first issue's message as the error's `message`, and
 * a form prints it under the field. Zod 4 rewrote every one of those sentences
 * — "Required" became "Invalid input: expected string, received undefined",
 * "String must contain at least 6 character(s)" became "Too small: expected
 * string to have >=6 characters" — and upgrading a validation library is not a
 * reason for every form in the product to start talking differently.
 *
 * So zod 3's English is restated here over zod 4's issue shapes and installed
 * as zod's global `customError`. That sits *below* any message a schema gives
 * itself (`.min(1, 'A first name is required.')` still wins) and *above* zod's
 * own locale, which is exactly where zod 3's default error map sat.
 *
 * It is installed by importing this module, which `index.ts` does, so the
 * server, the browser and every script that loads `@rowted/shared` say the
 * same thing for the same schema — the reason the schemas are shared at all.
 *
 * Anything zod 4 can say that zod 3 could not returns `undefined` and falls
 * through to zod's own wording.
 */
import { z } from 'zod';
/** zod 3's name for what it was handed (`getParsedType`). */
function receivedOf(data) {
    switch (typeof data) {
        case 'number': return Number.isNaN(data) ? 'nan' : 'number';
        case 'object': {
            if (Array.isArray(data))
                return 'array';
            if (data === null)
                return 'null';
            const thenable = data;
            if (typeof thenable.then === 'function' && typeof thenable.catch === 'function')
                return 'promise';
            if (data instanceof Map)
                return 'map';
            if (data instanceof Set)
                return 'set';
            if (data instanceof Date)
                return 'date';
            return 'object';
        }
        default: return typeof data;
    }
}
/** zod 3's `joinValues`: strings in single quotes, the rest as `join` writes them. */
const joinValues = (values, separator = ' | ') => values.map((value) => (typeof value === 'string' ? `'${value}'` : value)).join(separator);
const json = (value) => JSON.stringify(value, (_key, inner) => (typeof inner === 'bigint' ? inner.toString() : inner));
/** zod 4 names a few things more precisely than zod 3 did. */
const EXPECTED = { int: 'integer', record: 'object', tuple: 'array' };
const FORMAT = {
    ipv4: 'ip', ipv6: 'ip', cidrv4: 'cidr', cidrv6: 'cidr',
    guid: 'uuid', uuidv4: 'uuid', uuidv6: 'uuid', uuidv7: 'uuid',
};
function tooSmall(origin, minimum, inclusive, exact) {
    switch (origin) {
        case 'array':
            return `Array must contain ${exact ? 'exactly' : inclusive ? 'at least' : 'more than'} ${String(minimum)} element(s)`;
        case 'string':
            return `String must contain ${exact ? 'exactly' : inclusive ? 'at least' : 'over'} ${String(minimum)} character(s)`;
        case 'number':
        case 'int':
        case 'bigint':
            return `Number must be ${exact ? 'exactly equal to ' : inclusive ? 'greater than or equal to ' : 'greater than '}${String(minimum)}`;
        case 'date':
            return `Date must be ${exact ? 'exactly equal to ' : inclusive ? 'greater than or equal to ' : 'greater than '}${String(new Date(Number(minimum)))}`;
        default:
            return 'Invalid input';
    }
}
function tooBig(origin, maximum, inclusive, exact) {
    switch (origin) {
        case 'array':
            return `Array must contain ${exact ? 'exactly' : inclusive ? 'at most' : 'less than'} ${String(maximum)} element(s)`;
        case 'string':
            return `String must contain ${exact ? 'exactly' : inclusive ? 'at most' : 'under'} ${String(maximum)} character(s)`;
        case 'number':
        case 'int':
            return `Number must be ${exact ? 'exactly' : inclusive ? 'less than or equal to' : 'less than'} ${String(maximum)}`;
        case 'bigint':
            return `BigInt must be ${exact ? 'exactly' : inclusive ? 'less than or equal to' : 'less than'} ${String(maximum)}`;
        case 'date':
            return `Date must be ${exact ? 'exactly' : inclusive ? 'smaller than or equal to' : 'smaller than'} ${String(new Date(Number(maximum)))}`;
        default:
            return 'Invalid input';
    }
}
/** What zod 3 would have said about this issue, or nothing where it had no sentence for it. */
export function zod3Message(raw) {
    const issue = raw;
    const received = receivedOf(issue.input);
    switch (issue.code) {
        case 'invalid_type': {
            if (received === 'undefined')
                return 'Required';
            if (issue.expected === 'date' && issue.input instanceof Date)
                return 'Invalid date';
            const expected = EXPECTED[issue.expected ?? ''] ?? issue.expected;
            return `Expected ${expected}, received ${issue.expected === 'int' && received === 'number' ? 'float' : received}`;
        }
        case 'invalid_value': {
            const values = issue.values ?? [];
            const kind = issue.inst?._zod?.def?.type;
            if (kind === 'enum') {
                /* zod 3 checked the type first, so only a string got the "enum value" sentence. */
                if (typeof issue.input !== 'string') {
                    return received === 'undefined' ? 'Required' : `Expected ${joinValues(values)}, received ${received}`;
                }
                return `Invalid enum value. Expected ${joinValues(values)}, received '${issue.input}'`;
            }
            if (kind === 'literal') {
                return `Invalid literal value, expected ${values.length === 1 ? json(values[0]) : joinValues(values)}`;
            }
            return undefined;
        }
        case 'unrecognized_keys':
            return `Unrecognized key(s) in object: ${joinValues(issue.keys ?? [], ', ')}`;
        case 'invalid_union':
            return issue.options && issue.options.length > 0
                ? `Invalid discriminator value. Expected ${joinValues(issue.options)}`
                : 'Invalid input';
        case 'invalid_format':
            switch (issue.format) {
                case 'regex': return 'Invalid';
                case 'starts_with': return `Invalid input: must start with "${issue.prefix ?? ''}"`;
                case 'ends_with': return `Invalid input: must end with "${issue.suffix ?? ''}"`;
                case 'includes': return `Invalid input: must include "${issue.includes ?? ''}"`;
                default: return `Invalid ${FORMAT[issue.format ?? ''] ?? issue.format ?? 'input'}`;
            }
        case 'too_small':
            return tooSmall(issue.origin ?? '', issue.minimum, issue.inclusive ?? false, issue.exact ?? false);
        case 'too_big':
            return tooBig(issue.origin ?? '', issue.maximum, issue.inclusive ?? false, issue.exact ?? false);
        case 'not_multiple_of':
            return `Number must be a multiple of ${String(issue.divisor)}`;
        /* zod 3 reported a bad record key or set element as the inner issue itself. */
        case 'invalid_key':
        case 'invalid_element':
            return issue.issues?.[0]?.message ?? 'Invalid input';
        case 'custom':
            return 'Invalid input';
        default:
            return undefined;
    }
}
z.config({ customError: zod3Message });
//# sourceMappingURL=zod-messages.js.map